import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiPortalV1AssessmentsCompletedRetrieveQuery } from '../api';
import { assessmentPeriodApi, getMostRecentOpenPeriod } from '../api/assessmentPeriodApi';

import { StyledAddButton, StyledButton } from 'common/components/button/StyledButton';
import { Button, Dropdown, Grid, Modal } from 'semantic-ui-react';
import PositionedModal from 'common/components/PositionedModal';
import { ReminderModal } from './AssessmentSurveyDetailsModals';
import { PageContent, PageHeader, PageSection } from '../../../common/styles/page';
import { AssessmentPeriod } from '../types/AssessmentPeriod';

import DashboardAssessmentProgressRight from 'areas/dashboard/components/DashboardAssessmentProgressRight';
import DashboardCard from 'areas/dashboard/components/DashboardCard';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { authenticatedFetch } from 'common/utils/authFetch';
import AssessmentAdmin from './AssessmentAdmin';
import AssessmentPeriodModal from './AssessmentPeriodModal';
import AssessmentSurveyList from './AssessmentSurveyList';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';

import { ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_OPEN, ASSESSMENT_PERIOD_CLOSED } from '../constants';
import AssessmentSummaryCard from './AssessmentSummaryCard';
import { useSelector, useDispatch } from 'react-redux';
import userContextSlice from 'common/slices/userContextSlice';
import {
  getCurrentOrganizationId,
  getOrganizationAdmins,
  getOrganizationTeamLeaders,
  isCurrentUserStaffOrSuperAdmin,
} from '../../../common/selectors/auth';

import { AppConfig } from '../../../Config';
import LandingPage from 'common/layouts/AuthLayout/LandingPage';
import { useToasts } from 'common/toast';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { getSelectedAssessmentPeriodId } from 'common/selectors/auth';
import { employeeApi } from 'areas/settings/employee-management/api/employeeApi';

export const AssessmentOverview = () => {
  const navigate = useNavigate();
  const { addToast } = useToasts();

  const {
    data: assessmentsPeriods = [],
    isLoading,
    isFetching,
    isSuccess,
    isUninitialized,
  } = assessmentPeriodApi.useApiPortalV1AssessmentsListQuery();

  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const dispatch = useDispatch();

  const [createPeriod] = assessmentPeriodApi.useApiPortalV1AssessmentsCreateMutation();
  const [updatePeriod] = assessmentPeriodApi.useApiPortalV1AssessmentsPartialUpdateMutation();
  const [deletePeriod] = assessmentPeriodApi.useApiPortalV1AssessmentsDestroyMutation();
  const [sendRemindersToAll] = assessmentPeriodApi.useApiPortalV1AssessmentsSendRemindersCreateMutation();
  const assessmentPeriods = assessmentsPeriods;
  const isLoadingPeriods = isUninitialized || isLoading || isFetching;

  const organizationId = useSelector(getCurrentOrganizationId);
  const { data: rosterReviewData } = employeeApi.useGetRosterReviewQuery(
    { organizationId: organizationId || 0 },
    { skip: !organizationId }
  );
  const isUserAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const teamLeaders = useSelector(getOrganizationTeamLeaders);
  const orgAdmins = useSelector(getOrganizationAdmins);
  const canManageAssessments = isUserAdmin;
  const [isDownloadingCompleted, setIsDownloadingCompleted] = useState(false);

  //  const [assessmentPeriodId, setAssessmentPeriodId] = useState<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [, setEditData] = useState<Partial<AssessmentPeriod>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRemindAllModalOpen, setIsRemindAllModalOpen] = useState(false);
  const [isAdminView, setIsAdminView] = useState<boolean>(false);
  const [, setAlertMessage] = useState<{ no: number; text: string | null }>({
    no: 0,
    text: null,
  });

  const { setTopBarContent } = useContext(TopBarContext);

  useEffect(() => {
    setTopBarContent(<TopBarControls status={[ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_CLOSED, ASSESSMENT_PERIOD_OPEN]} />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);


  const updateSelectedAssessmentPeriodById = React.useCallback((id?: number | undefined) => {
    // Look up the period object from the fetched list
    const selected = assessmentPeriods?.find(p => p.id === id);
    if (selected) {
      dispatch(
        userContextSlice.actions.setSelectedAssessmentPeriod({
          selectedAssessmentPeriod: selected,
          assessmentPeriods,
        })
      );
    } else {
      // Fallback to most recent open or first period, or clear if none exist
      const fallback = assessmentPeriods && (getMostRecentOpenPeriod(assessmentPeriods) ?? assessmentPeriods[0]);
      if (fallback) {
        dispatch(
          userContextSlice.actions.setSelectedAssessmentPeriod({
            selectedAssessmentPeriod: fallback,
            assessmentPeriods,
          })
        );
      } else {
        // No periods available — clear selection
        dispatch(
          // Use `any` payload shape for legacy reducer compatibility
          userContextSlice.actions.setSelectedAssessmentPeriod({ selectedAssessmentPeriod: undefined, assessmentPeriods: [] } as any)
        );
      }
    }
  }, [assessmentPeriods, dispatch]);




  // Set default period when loaded
  useEffect(() => {
    if (!isLoadingPeriods && assessmentPeriods?.length && !assessmentPeriodId) {
      const defaultPeriod = getMostRecentOpenPeriod(assessmentPeriods);
      if (defaultPeriod) updateSelectedAssessmentPeriodById(defaultPeriod.id);
    }
  }, [isLoadingPeriods, assessmentPeriods, assessmentPeriodId, updateSelectedAssessmentPeriodById]);

  useEffect(() => {
    if (!isSuccess) return;
    if (assessmentPeriods.length && !assessmentPeriodId) {
      const def = getMostRecentOpenPeriod(assessmentPeriods) ?? assessmentPeriods[0];
      updateSelectedAssessmentPeriodById(def.id);
    }
  }, [isSuccess, assessmentPeriods, assessmentPeriodId, updateSelectedAssessmentPeriodById]);


  // Memoize selected period
  const assessmentPeriod = useMemo(
    () =>
      assessmentPeriods.find(p => p.id === assessmentPeriodId) ??
      getMostRecentOpenPeriod(assessmentPeriods) ??
      assessmentPeriods[0] ??
      null,
    [assessmentPeriods, assessmentPeriodId]
  );

  const previousAssessmentCloseDate = useMemo(() => {
    const closedPeriods = assessmentPeriods.filter(period => period.status === ASSESSMENT_PERIOD_CLOSED);
    if (closedPeriods.length === 0) {
      return null;
    }

    const sortedByEndDate = [...closedPeriods].sort((a, b) => {
      const aDate = a.end_date_time ? new Date(a.end_date_time).getTime() : 0;
      const bDate = b.end_date_time ? new Date(b.end_date_time).getTime() : 0;
      return bDate - aDate;
    });

    return sortedByEndDate[0]?.end_date_time ?? null;
  }, [assessmentPeriods]);

  const showRosterReviewWarning = useMemo(() => {
    if (!previousAssessmentCloseDate) {
      return false;
    }

    const previousCloseDateValue = new Date(previousAssessmentCloseDate);
    if (Number.isNaN(previousCloseDateValue.getTime())) {
      return false;
    }

    const completedAtRaw = rosterReviewData?.cycle?.completed_at;
    if (!completedAtRaw) {
      return true;
    }

    const completedAt = new Date(completedAtRaw);
    if (Number.isNaN(completedAt.getTime())) {
      return true;
    }

    return completedAt < previousCloseDateValue;
  }, [previousAssessmentCloseDate, rosterReviewData]);

  const { data: assessment_completion_data } = useApiPortalV1AssessmentsCompletedRetrieveQuery(
    { assessmentPeriodId: assessmentPeriod?.id!! },
    { skip: !assessmentPeriod?.id }
  );


  const handleDownloadCompletedExport = async () => {
    try {
      setIsDownloadingCompleted(true);

      // Construct the URL with query parameters
      const baseUrl = AppConfig.api.apiUrl;
      const endpoint = `/api/portal/v1/assessments/export-responses/`;
      const url = `${baseUrl}${endpoint}?assessment_period_id=${assessmentPeriod?.id}`;

      const response = await authenticatedFetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-NULOGIC-ORGANIZATION-ID': organizationId.toString(),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download survey completion data: ${response.status} ${response.statusText}`);
      }

      // Get the blob from the response
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `member_surveys_completed.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      addToast('Export downloaded successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Failed to download completed survey export:', error);
      addToast('Failed to download completed survey export', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDownloadingCompleted(false);
    }
  };




  // Delete Assessment Period
  const handleDelete = useCallback(() => {
    if (!canManageAssessments) {
      setAlertMessage({ no: 1, text: "You don't have permission to delete assessment periods." });
      return;
    }
    setIsDeleteModalOpen(true);
  }, [canManageAssessments]);

  const confirmDelete = useCallback(() => {
    if (!canManageAssessments) {
      setAlertMessage({ no: 1, text: "You don't have permission to delete assessment periods." });
      return;
    }
    if (assessmentPeriod && assessmentPeriod.id !== undefined) {
      deletePeriod({
        id: assessmentPeriod.id!,
        debriefScheduleId: (assessmentPeriod as any).debriefScheduleId,
      });
    }
    setIsDeleteModalOpen(false);
    // After delete, select the most recent open period from remaining periods
    const remaining = assessmentPeriods?.filter(p => p.id !== assessmentPeriod?.id) ?? [];
    const mostRecentOpenPeriod = getMostRecentOpenPeriod(remaining);
    if (mostRecentOpenPeriod) {
      updateSelectedAssessmentPeriodById(mostRecentOpenPeriod.id);
    } else if (remaining.length) {
      updateSelectedAssessmentPeriodById(remaining[0].id);
    } else {
      updateSelectedAssessmentPeriodById(undefined);
    }
  }, [
    canManageAssessments,
    assessmentPeriod,
    deletePeriod,
    assessmentPeriods,
    updateSelectedAssessmentPeriodById,
  ]);

  // Confirm send reminders to ALL participants
  const confirmSendRemindersToAll = async () => {
    if (!assessmentPeriod?.id) return;
    try {
      const result = await sendRemindersToAll({ id: assessmentPeriod.id }).unwrap();
      if (result.success) {
        addToast(`Successfully sent ${result.count} reminder${result.count !== 1 ? 's' : ''}`, { appearance: 'success' });
      } else {
        addToast(`Failed to send reminders: ${result.error || 'Unknown error'}`, { appearance: 'error' });
      }
    } catch (error: any) {
      addToast(`Error sending reminders: ${error?.data?.error || error?.message || 'Unknown error'}`, { appearance: 'error' });
    } finally {
      setIsRemindAllModalOpen(false);
    }
  };

  // Edit Assessment Period
  const handleEdit = useCallback(() => {
    if (!canManageAssessments) {
      setAlertMessage({ no: 1, text: "You don't have permission to edit assessment periods." });
      return;
    }
    if (assessmentPeriod) {
      setEditData({
        ...assessmentPeriod,
        startDateTime: new Date(assessmentPeriod?.start_date_time ?? ''),
        endDateTime: new Date(assessmentPeriod?.end_date_time ?? ''),
      });
    }
    setIsEditModalOpen(true);
  }, [canManageAssessments, assessmentPeriod]);

  function generateSurveyLink(): string {
    const briefToken = (assessmentPeriod as any)?.brief_token;
    if (!briefToken) {
      return '';
    }
    return `${AppConfig.surveyUrl}/ediV2/${briefToken}`;
  }

  // Add Assessment Period
  const onSave = useCallback(
    async (newAssessmentPeriod: AssessmentPeriod) => {
      if (!canManageAssessments) {
        setAlertMessage({ no: 1, text: "You don't have permission to create assessment periods." });
        return;
      }
      const currentDate = new Date();
      const startDate = newAssessmentPeriod?.startDateTime
        ? new Date(newAssessmentPeriod.startDateTime)
        : new Date(currentDate.getDate() + 7);

      const endDate = newAssessmentPeriod?.endDateTime
        ? new Date(newAssessmentPeriod.endDateTime)
        : new Date(currentDate.getDate() + 14);

      try {
        const response = await createPeriod({
          assessmentPeriod: {
            organization: Number(organizationId) || 0,
            name: newAssessmentPeriod.name,
            start_date_time: startDate.toISOString(),
            end_date_time: endDate.toISOString(),
            min_followup_questions: newAssessmentPeriod.minFollowupQuestions,
            max_followup_questions: newAssessmentPeriod.maxFollowupQuestions,
            outcome_question: newAssessmentPeriod.outcomeQuestionId,
            survey_question: newAssessmentPeriod.surveyQuestionId,
            reminder_enabled: newAssessmentPeriod.reminderEnabled ?? false,
            reminder_schedule: newAssessmentPeriod.reminderSchedule ?? null,
          },
        }).unwrap();
        // Immediately select the newly created period and include it in the local assessment list
        dispatch(
          userContextSlice.actions.setSelectedAssessmentPeriod({
            selectedAssessmentPeriod: response,
            assessmentPeriods: [response, ...(assessmentPeriods || [])],
          })
        );
      } catch (error) {
        console.error('Failed to create assessment period:', error);
      }
    },
    [canManageAssessments, createPeriod, organizationId, dispatch, assessmentPeriods]
  );

  // Wait for data to load from API
  if (isLoadingPeriods) {
    return <LoadingSpinner />;
  }

  if (!assessmentPeriod && assessmentPeriods.length > 0) {
    const mostRecent = getMostRecentOpenPeriod(assessmentPeriods) ?? assessmentPeriods[0];
    if (mostRecent) updateSelectedAssessmentPeriodById(mostRecent.id);
    return <LoadingSpinner />;
  }

  // If there are no assessment periods, create one
  if (isSuccess && assessmentPeriods.length === 0 && canManageAssessments) {
    return (
      <>
        <AssessmentPeriodModal
          open={open}
          setOpen={setOpen}
          mode="create"
          onSave={onSave}
          showRosterReviewWarning={showRosterReviewWarning}
        />

        <PageContent>
          <LandingPage
            title="Create an Assessment!"
            subtitle="If you've imported members, start an assessment to collect organization sentiment data..."
            buttonLabel="New Assessment"
            onClick={() => setOpen(true)}
          />
        </PageContent>
      </>
    );
  }

  // If there are no assessment periods, but no permission to create one, show message
  if (isSuccess && assessmentPeriods.length === 0 && !canManageAssessments) {
    return (
      <>
        <PageContent>
          <LandingPage
            title="Start an Assessment!"
            subtitle="Supply member information to nuLogic and we can schedule an assessment and send out surveys..."
          />
        </PageContent>
      </>
    );
  }

  // If there are assessment periods but none selected, select the most recent one
  if (!assessmentPeriod) {
    const mostRecentOpenPeriod = getMostRecentOpenPeriod(assessmentPeriods);
    if (mostRecentOpenPeriod) {
      updateSelectedAssessmentPeriodById(mostRecentOpenPeriod.id);
      return <LoadingSpinner />;
    }
  }

  if (isAdminView && isUserAdmin) {
    return (
      <>
        <PageContent data-testid="assessments-page">
          <PageHeader
            title="Assessment Period Admin"
            controls={
              <>
                <StyledButton label="Back" width="50px" onClick={() => setIsAdminView(false)} />
              </>
            }
          />
          <AssessmentAdmin
            data={assessment_completion_data}
            assessmentId={assessmentPeriod?.id ?? 0}
            onSetSelectedAssessment={id => {
              updateSelectedAssessmentPeriodById(id);
            }}
            selectedAssessmentPeriod={assessmentPeriod}
          />
        </PageContent>
      </>
    );
  }

  // If there are assessment periods, show the main overview
  return (
    <>
      <PageContent data-testid="assessments-page">
        <PageHeader
          title={'Assessment: ' + (assessmentPeriod?.name || 'Assessment Overview')}
          controls={
            <>
                  {canManageAssessments && (
                    <StyledAddButton
                      label="Add Assessment Period"
                      width="200px"
                      onClick={() => setOpen(true)}
                    />
                  )}

                  <Dropdown
                    button
                    icon="setting"
                    floating
                    className="icon"
                    style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                    direction="left"
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        icon="file alternate outline"
                        text={'Preview Survey'}
                        onClick={() => {
                          const url = generateSurveyLink();
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                      />
                      <Dropdown.Item
                        icon="send"
                        text="Remind Participants"
                        disabled={!assessmentPeriod ||
                          !(assessmentPeriod?.status === ASSESSMENT_PERIOD_IN_PROGRESS)}
                        onClick={e => {
                          e.stopPropagation();
                          setIsRemindAllModalOpen(true);
                        }}
                      />
                      <Dropdown.Item
                        icon="download"
                        text={isDownloadingCompleted ? 'Downloading...' : 'Members Completed (CSV)'}
                        onClick={e => {
                          e.stopPropagation();
                          handleDownloadCompletedExport();
                        }}
                      />
                      {canManageAssessments && (
                        <>
                          <Dropdown.Item
                            icon="write"
                            text="Edit"
                            disabled={
                              assessmentPeriod?.status !== ASSESSMENT_PERIOD_OPEN &&
                              assessmentPeriod?.status !== ASSESSMENT_PERIOD_IN_PROGRESS
                            }
                            onClick={e => {
                              e.stopPropagation();
                              handleEdit();
                            }}
                          />
                          <Dropdown.Item
                            icon="trash"
                            text="Delete"
                            disabled={
                              assessmentPeriod?.status !== ASSESSMENT_PERIOD_OPEN
                            }
                            onClick={e => {
                              e.stopPropagation();
                              handleDelete();
                            }}
                          />
                        </>
                      )}
                      {isUserAdmin && (
                        <>
                          <Dropdown.Divider />
                          <Dropdown.Item
                            icon="user secret"
                            text="Admin"
                            onClick={e => {
                              e.stopPropagation();
                              setIsAdminView(true);
                            }}
                          />
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </>
          }
        />
        <PageSection>
          <PageSection padbottom>
            <Grid columns={2}>
              <Grid.Column width={8}>
                <DashboardCard
                >
                  <AssessmentSummaryCard
                    data={assessment_completion_data}
                    selectedAssessmentPeriod={assessmentPeriod}
                  />
                </DashboardCard>
              </Grid.Column>
              <Grid.Column width={8}>
                <DashboardCard>
                  {assessment_completion_data && (
                    <DashboardAssessmentProgressRight
                      assessment_progress_data={assessment_completion_data as any}
                      start_date={new Date(assessmentPeriod?.start_date_time || '')}
                      end_date={new Date(assessmentPeriod?.end_date_time || '')}
                    />
                  )}
                </DashboardCard>
              </Grid.Column>
            </Grid>
          </PageSection>
        </PageSection>
        <PageSection>
          <AssessmentSurveyList
            selectedAssessmentPeriodId={assessmentPeriod?.id ?? 0}
            assessmentPeriodStatus={assessmentPeriod?.status}
            assessmentPeriodStartDate={assessmentPeriod?.start_date_time}
            assessmentPeriodEndDate={assessmentPeriod?.end_date_time}
            onViewDetails={(id: number) => navigate(`/app/assessments/${id}`)}
            onDelete={handleDelete}
            canManageAssessments={canManageAssessments}
            teamLeaders={teamLeaders}
            orgAdmins={orgAdmins}
          />
        </PageSection>
      </PageContent>

      <AssessmentPeriodModal
        open={open}
        setOpen={setOpen}
        mode="create"
        onSave={onSave}
        showRosterReviewWarning={showRosterReviewWarning}
      />

      <AssessmentPeriodModal
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        mode="edit"
        initial={{
          name: assessmentPeriod?.name,
          startDateTime: new Date(assessmentPeriod?.start_date_time ?? ''),
          endDateTime: new Date(assessmentPeriod?.end_date_time ?? ''),
          minFollowupQuestions: (assessmentPeriod as any)?.min_followup_questions,
          maxFollowupQuestions: (assessmentPeriod as any)?.max_followup_questions,
          outcomeQuestionId: (assessmentPeriod as any)?.outcome_question,
          surveyQuestionId: (assessmentPeriod as any)?.survey_question,
          reminderEnabled: (assessmentPeriod as any)?.reminder_enabled ?? false,
          reminderSchedule: (() => {
            const schedule = (assessmentPeriod as any)?.reminder_schedule;
            // Handle both string (cron) and legacy dict formats
            return typeof schedule === 'string' ? schedule : (schedule?.cron || undefined);
          })(),
        }}
        onSave={async (data) => {
          if (!assessmentPeriod?.id) return;
          const result = await updatePeriod({
            id: assessmentPeriod.id,
            patchedAssessmentPeriod: {
              name: data?.name,
              start_date_time: String(data?.startDateTime?.toISOString()),
              end_date_time: String(data?.endDateTime?.toISOString()),
              reminder_enabled: data?.reminderEnabled,
              reminder_schedule: data?.reminderSchedule,
            } as any,
          });

          // Check if the mutation succeeded
          if ('error' in result) {
            console.error('Failed to update assessment period:', result.error);
            throw new Error('Failed to update assessment period');
          }
        }}
      />

      <ReminderModal
        open={isRemindAllModalOpen}
        name={'all participants'}
        email={''}
        onClose={() => setIsRemindAllModalOpen(false)}
        onConfirm={confirmSendRemindersToAll}
      />

      <PositionedModal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} size="small">
        <Modal.Header>Delete Assessment Period</Modal.Header>
        <Modal.Content>Are you sure you want to delete this assessment period?</Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete}>Delete</Button>
        </Modal.Actions>
      </PositionedModal>
    </>
  );
};

export default AssessmentOverview;
